import { formatDate } from './format';
import { lib } from './lib';

const setTruncation = (ctr) => {
	// Add an event listener to the truncated elements.
	const truncatedElms = ctr.querySelectorAll(lib.selectors.truncate);

	truncatedElms.forEach((elm) => {
		// Get the height of the element when the text is not yet truncated.
		const expandedInnerRect = elm.getBoundingClientRect();
		elm.style.setProperty('--truncate-height-expanded', `${expandedInnerRect.height}px`);

		// Add a class to the element to indicate that the text is truncated.
		elm.classList.add(lib.classes.closed, lib.classes.clamp);

		elm.addEventListener('click', () => {
			elm.classList.toggle(lib.classes.closed);

			if (elm.classList.contains(lib.classes.closed)) {
				setTimeout(() => elm.classList.add(lib.classes.clamp), 300);
			} else {
				elm.classList.remove(lib.classes.clamp);
			}
		});
	});
};

const getSchemaAttribute = (itemprop, isoDatetime, inline = true) => {
	const attributes = `itemprop="${itemprop}" content="${isoDatetime}"`;

	if (inline) {
		return attributes;
	} else {
		return `<span ${attributes}></span>`;
	}
};

const getDates = (startDateTime, endDateTime, anyTimeLabel, isAlwaysAvailable = false) => {
	if (!startDateTime) {
		startDateTime = new Date().toISOString();
	}

	const formattedString = formatDate(startDateTime, endDateTime);
	const schemaStartDate = getSchemaAttribute('startDate', startDateTime);
	const schemaEndDate = endDateTime ? getSchemaAttribute('endDate', endDateTime, false) : '';

	const dateHtml = `<span ${schemaStartDate}>${formattedString}</span>${schemaEndDate}`;
	const alwaysAvailableHtml = `<span ${schemaStartDate}>${anyTimeLabel}</span><meta itemprop="eventStatus" content="https://schema.org/EventScheduled">`;

	return `<div class="${lib.classes.date}"><span class="${lib.classes.icon}">event</span>${isAlwaysAvailable ? alwaysAvailableHtml : dateHtml}</div>`;
};

const getAddress = (city) => {
	return `
	<div itemprop="address" itemscope itemtype="https://schema.org/PostalAddress">
		, <span itemprop="addressLocality">${city}</span>
	</div>
	`;
};

const getBadges = (date) => {
	let badges = '';

	date.categories.forEach((category) => {
		badges += `<span class="${lib.classes.badge}">${category}</span>`;
	});

	return `<div class="${lib.classes.badges}">${badges}</div>`;
};

const getLocation = (location, online) => {
	const name = `
		<span class="${lib.classes.icon}">location_on</span>
		<span itemprop="name">${location.online ? online : location.name}</span>
		${!location.online ? getAddress(location.city) : ''}
	`;

	return `
	<div itemprop="location" itemscope itemtype="https://schema.org/Place" class="${lib.classes.location}">
		${location.maps_url !== '' ? `<a href="${location.maps_url}" target="_blank" rel="noopener">${name}</a>` : name}
	</div>
	`;
};

const createAnchorTag = (link) => {
	return `<a href="${link.url}" target="_blank" class="btn btn--${link.indicator}" itemprop="url">${link.label}</a>`;
};

const createNavEventFooter = (link) => {
	return `<footer class="event__footer"><span class="event__link">${link.label}</span><span class="event__indicator" aria-hidden="true"></span></footer>`;
};

const getTheLink = (links, dates) => {
	if (dates.end_datetime === null || dates.start_datetime === null) {
		return links.always.url !== '' ? links.always : null;
	}

	const currentDate = new Date();
	const endDate = new Date(dates.end_datetime);

	if (endDate > currentDate) {
		// Check if the event is still in the future, then return a link that is relevant to the future of the event.
		return links.before.url !== '' ? links.before : null;
	} else {
		// Check if the event is in the past, then return a link that is relevant to the past of the event.
		return links.after.url !== '' ? links.after : null;
	}
};

const getSchemaImage = (thumb) => {
	if (!thumb) {
		return '';
	}

	// Add schema attribute to the first image tag if it is not already set.
	return thumb.replace(/<img\b(?![^>]*\bitemprop=)/, '<img itemprop="image" ');
};

const getPageDataStructure = (date, labels) => {
	const { title, thumb, links, dates, excerpt, location } = date;
	const link = getTheLink(links, dates);
	const linkHtml = link ? createAnchorTag(link) : '';
	const isAlwaysAvailable = dates.start_datetime === null && dates.end_datetime === null;
	const schemaImage = getSchemaImage(thumb);

	return `
	<div class="row event" itemscope itemtype="https://schema.org/Event">
		<div class="col-xl-5">
	  		<div class="event__thumb">${schemaImage}</div>
		</div>
		<div class="col-xl-7">
			${getBadges(date)}
			<h5 itemprop="name">${title}</h5>
			<div class="${lib.classes.meta}">
				${getDates(dates.start_datetime, dates.end_datetime, labels.any_time, isAlwaysAvailable)}
				${location && (dates.end_datetime !== null || isAlwaysAvailable) ? getLocation(location, labels.online) : ''}
			</div>
			<div itemprop="description" class="${lib.classes.description} ${excerpt.length > 70 && lib.classes.truncate}">
				<div class="${lib.classes.descriptionInner}">${excerpt.text}</div>
			</div>
			${linkHtml}
		</div>
	</div>`;
};

const getNavDataStructure = (date) => {
	const { title, thumb, links, dates } = date;
	const link = getTheLink(links, dates);
	const linkHtml = link ? createNavEventFooter(link) : '';
	const isAlwaysAvailable = dates.start_datetime === null && dates.end_datetime === null;

	return `
	${link ? `<a class="event event--${link.indicator}" href="${link.url}" target="_blank" title="${link.label}">` : '<div class="event">'}
			${getBadges(date)}
			<div class="event__thumb">${thumb}</div>
			<div class="${lib.classes.meta}">
				${!isAlwaysAvailable ? getDates(dates.start_datetime, dates.end_datetime) : ''}
			</div>
			<p class="stage-event__title h6">${title}</p>
			${linkHtml}
	${link ? `</a>` : '</div>'}`;
};

const renderEvents = (node, labels, events, isNav = false) => {
	// Check if events are already rendered to avoid duplicates.
	if (node.querySelector(`.${lib.classes.ctr}`)) {
		return;
	}

	// Create wrapper for each event section.
	const wrapper = document.createElement('div');
	wrapper.setAttribute('class', lib.classes.ctr);

	events.forEach((date) => {
		wrapper.innerHTML += isNav ? getNavDataStructure(date) : getPageDataStructure(date, labels);
	});

	node.append(wrapper);
};

const renderNoEvents = (ctr, message) => {
	// Check if no-events message is already rendered to avoid duplicates.
	if (ctr.querySelector(`.${lib.classes.noEvents}`)) {
		return;
	}

	const noEvents = document.createElement('div');
	noEvents.setAttribute('class', lib.classes.noEvents);
	const paragraph = document.createElement('p');
	paragraph.setAttribute('class', lib.classes.infobox);
	paragraph.innerText = message;

	noEvents.append(paragraph);
	ctr.append(noEvents);
};

export { getAddress, getBadges, getNavDataStructure, getPageDataStructure, renderEvents, renderNoEvents, setTruncation };
